import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import tw from 'twin.macro';

import { usePrepaidGetStatusQuery } from '@/app/api/prepaid';
import ApplePayIcon from '@/assets/images/icons/ApplePayIcon';
import GarminPayIcon from '@/assets/images/icons/GarminPayIcon';
import GooglePayIcon from '@/assets/images/icons/GooglePayIcon';
import SamsungPayIcon from '@/assets/images/icons/SamsungPayIcon';
import BlueCard from '@/assets/images/prepaid_blue.webp';
import GreenCard from '@/assets/images/prepaid_green.webp';
import PinkCard from '@/assets/images/prepaid_pink.webp';
import SmartPay from '@/assets/images/smart_pay.webp';
import { Button, Modal, SubBody, SubTitle, Title } from '@/components';

const payments = [
  {
    icon: <GooglePayIcon />,
    link: 'https://bankffin.kz/ru/page/39-kak-podklyuchit-kartu-k-google-pay-na-smartfone'
  },
  {
    icon: <ApplePayIcon />,
    link: 'https://bankffin.kz/ru/page/36-kak-podklyuchit-kartu-k-apple-pay-na-smartfone'
  },
  {
    icon: <SamsungPayIcon />,
    link: 'https://bankffin.kz/ru/cards/samsung-pay-info'
  },
  {
    icon: <GarminPayIcon />,
    link: ''
  }
];

const returnCard = number => {
  switch (number) {
    case 'PREPAID_50':
      return <img src={PinkCard} alt='pink-card' width={300} />;
    case 'PREPAID_100':
      return <img src={GreenCard} alt='green-card' width={300} />;
    case 'PREPAID_200':
      return <img src={BlueCard} alt='blue-card' width={300} />;
    default:
      break;
  }
};

const convertPan = pan => {
  let newString = '';
  pan?.replaceAll('.', '•');
  newString += pan?.slice(0, 4) + ' ••••' + ' •••• ' + pan?.slice(7, pan.length);
  return newString;
};

function PrepaidSuccessPage() {
  const [stopPooling, setStopPooling] = useState(false);
  const [openModal, setOpenModal] = useState(true);

  const navigate = useNavigate();

  const { data, isLoading } = usePrepaidGetStatusQuery(null, { pollingInterval: stopPooling ? 0 : 10000 });

  useEffect(() => {
    if (data?.data?.pan_card) {
      setStopPooling(true);
      setOpenModal(false);
    }
  }, [data]);

  return (
    <div tw='flex flex-col justify-center items-center gap-5 sm:p-0 p-5'>
      {isLoading ? (
        <div tw='bg-secondary w-[300px] h-[170px] animate-pulse rounded-2xl'></div>
      ) : (
        returnCard(data?.data?.product_code)
      )}
      <SubTitle
        text={
          <span>
            Цифровая предоплаченная карта № {data?.data?.number ?? '...'} <br /> успешно выпущена <br /> <br />
            {isLoading ? (
              <div tw='bg-secondary w-[150px] h-[40px] animate-pulse rounded-xl'></div>
            ) : (
              convertPan(data?.data?.pan_card)
            )}
          </span>
        }
        twStyle={tw`text-center`}
        variant={'bold'}
      />
      <SubBody
        text={
          <div>
            <div>Сохраните этот номер и ожидайте SMS с остальными данными карты.</div>
            <div>Время получения SMS может занять до 30 минут.</div>
          </div>
        }
        twStyle={tw`text-secondary text-center`}
      />
      <div tw={'flex flex-col'}>
        <Button onClick={() => navigate('/my-documents-main?tab=PREPAID_CARD')} variant={'ghost'}>
          Перейти в «Мои документы»
        </Button>
        <SubBody text={'Все ваши карты, баланс и выписка по операциям'} twStyle={tw`text-secondary mt-2 mb-10`} />
      </div>
      <div tw='bg-secondary p-5 rounded-xl sm:gap-0 gap-4 flex items-center w-full justify-center'>
        <WarningIcon />
        <SubBody
          text={'Карта привязана к вашему номеру телефона, поэтому храните его и данные карты в секрете.'}
          twStyle={tw`text-secondary sm:-ml-5`}
        />
      </div>
      <div tw='bg-secondary flex flex-col items-center p-5 rounded-2xl w-full'>
        <img src={SmartPay} alt='smart pay' />
        <SubTitle text={'Добавьте карту в электронный кошелёк'} twStyle={tw`text-center`} variant={'bold'} />
        <SubBody text={'и оплачивайте покупки смартфоном'} twStyle={tw`text-secondary mt-5`} />
        <div tw='sm:flex gap-3 mt-5 sm:w-auto w-full items-center'>
          {payments?.map(payment => (
            <a href={payment.link} tw='cursor-pointer' target='_blank' rel='noreferrer' key={payment.link}>
              <div tw='border border-button rounded-xl py-3 px-5 w-full flex items-center justify-center sm:mt-0 mt-2'>
                {payment.icon}
              </div>
            </a>
          ))}
        </div>
      </div>

      <Modal
        open={openModal}
        setOpen={setOpenModal}
        outsideClose={false}
        withCloseIcon={false}
        withBlur
        twStyle={tw`rounded-[20px]`}
      >
        <ClipLoader color='rgb(42, 134, 64)' size={70} speedMultiplier={0.6} />
        <div>
          <Title text={<span>Идет процесс создания карточки</span>} variant={'bold'} />
          <SubTitle
            twStyle={tw`sm:text-s18 text-s14`}
            text={
              <span>
                <br />
                Процесс займет не более 5 минут
                <br /> Пожалуйста, подождите...
              </span>
            }
          />
        </div>
      </Modal>
    </div>
  );
}

const WarningIcon = () => {
  return (
    <svg width={50} height={24} fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zM12 17v-5'
        stroke={'#EEBB08'}
        fill='none'
        strokeWidth={1.5}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M12.006 8h-.01'
        stroke={'#EEBB08'}
        fill='none'
        strokeWidth={2}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};

export default PrepaidSuccessPage;
